import { useState, useEffect, useMemo } from "react";
import { useIdleTimer as useIdleTimerLib } from "react-idle-timer";

const IDLE_TIMEOUT = 30 * 60 * 1000; // 30 minutes in milliseconds
const WARNING_TIMEOUT = 5 * 60 * 1000; // 5 minutes in milliseconds

interface UseIdleTimerReturn {
    showWarning: boolean;
    remainingTime: number;
    handleLogout: () => void;
    handleStayActive: () => void;
}

export const useIdleTimer = (onLogout: () => void): UseIdleTimerReturn => {
    const [showWarning, setShowWarning] = useState(false);
    const [remainingTime, setRemainingTime] = useState(0);

    // FOR TESTING PURPOSES: Allow timeout values to be set via URL parameters
    // e.g., ?idleTimeout=60&warningTimeout=10
    // where values are in seconds
    const { idleTimeout, warningTimeout } = useMemo(() => {
        const params = new URLSearchParams(window.location.search);
        const idleSeconds = params.get("idleTimeout");
        const warningSeconds = params.get("warningTimeout");

        return {
            idleTimeout: idleSeconds
                ? parseInt(idleSeconds, 10) * 1000
                : IDLE_TIMEOUT,
            warningTimeout: warningSeconds
                ? parseInt(warningSeconds, 10) * 1000
                : WARNING_TIMEOUT,
        };
    }, []);

    const { getRemainingTime, activate } = useIdleTimerLib({
        timeout: idleTimeout,
        promptBeforeIdle: warningTimeout,
        onPrompt: () => setShowWarning(true),
        onIdle: onLogout,
        onActive: () => setShowWarning(false),
        throttle: 500,
    });

    useEffect(() => {
        const interval = setInterval(() => {
            setRemainingTime(Math.ceil(getRemainingTime() / 1000));
        }, 500);

        return () => clearInterval(interval);
    }, [getRemainingTime]);

    return {
        showWarning,
        remainingTime,
        handleLogout: onLogout,
        handleStayActive: activate,
    };
};
